import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";

/**
 * The ApiService class handles all network requests to the API gateway
 *
 * @export
 * @class ApiService
 *
 * @example
 * <script>
 *  ApiService.GET('/posts').subscribe({
 *    next: response => {}, // Assign data to variable here
 *    error: err => {}
 *  });
 * </script>
 */
@Injectable({
  providedIn: "root",
})
export class ApiService {
  /**
   * Default headers for each API request
   * @constant
   * @type {object}
   */
  getHttpOptions(addBearer = false, additionalHeaders = null) {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          addBearer && this.authService.getUserToken()
            ? "Bearer " + this.authService.getUserToken()
            : "",
        ...additionalHeaders,
      }),
    };
  }

  /**
   * Creates an instance of ApiService.
   * @param {HttpClient} http The injectable http class from Angular
   * @memberof ApiService
   */
  constructor(public http: HttpClient, private authService: AuthService) {}

  /**
   * Getting data through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {string} [query=null] The query string for specific requests (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  GET(
    apiRoute: string,
    query: string = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    //Build url incase query's are passed into the method
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;
    query !== null ? requestUrl + "/" + query : null;

    //Execute GET request with error handler
    return this.http
      .get(requestUrl, this.getHttpOptions(false, additionalHeaders))
      .pipe(catchError(this.handleError));
  }

  /**
   * Getting data through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {string} [query=null] The query string for specific requests (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  AuthGET(
    apiRoute: string,
    query: string = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    //Build url incase query's are passed into the method
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;
    query !== null ? requestUrl + "/" + query : null;

    //Execute GET request with error handler
    return this.http
      .get(requestUrl, this.getHttpOptions(true, additionalHeaders))
      .pipe(catchError(this.handleError));
  }

  /**
   * Pushing data through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {*} [body=null] The body/data for the request (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  POST(
    apiRoute: string,
    body: any = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;

    //Execute POST request with error handler
    return this.http
      .post(requestUrl, body, this.getHttpOptions(false, additionalHeaders))
      .pipe(catchError(this.handleError));
  }

  /**
   * Deleting data through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {string} [query=null] The query string for specific requests (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  DELETE(
    apiRoute: string,
    query: string = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    //Build url incase query's are passed into the method
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;
    query !== null ? requestUrl + "/" + query : null;

    //Execute PUT request with error handler
    return this.http
      .delete(requestUrl, this.getHttpOptions(false, additionalHeaders))
      .pipe(catchError(this.handleError));
  }

  /**
   * Pushing a resource file through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {*} [body=null] The body/data for the request (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  PUT(
    apiRoute: string,
    body: any = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;
    //Execute PUT request with error handler
    return this.http
      .put(requestUrl, body, this.getHttpOptions(true, additionalHeaders))
      .pipe(catchError(this.handleError));
  }

  /**
   * Updating partial resources through the API gateway
   *
   * @param {string} apiRoute The request url
   * @param {*} [body=null] The body/data for the request (optional)
   * @returns {Observable<any>} The observable with generic error handling
   * @memberof ApiService
   */
  PATCH(
    apiRoute: string,
    body: any = null,
    auth: boolean = false,
    additionalHeaders: any = null
  ): Observable<any> {
    let requestUrl: string = apiRoute.includes("//")
      ? apiRoute
      : auth
      ? environment.api_auth_url + apiRoute
      : environment.api_base_url + apiRoute;
    //Execute PATCH request with error handler
    return this.http
      .patch(requestUrl, body, this.getHttpOptions())
      .pipe(catchError(this.handleError));
  }

  /**
   * Method to handle network request errors
   *
   * @private
   * @param {HttpErrorResponse} error
   * @returns The errors are logged to console by default. 3rd party integrations should be implemented here
   * @memberof ApiService
   */
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      /**
       * A client-side or network error occurred. Handle it accordingly.
       */
      // console.error('An error occurred:', error.error.message);
    } else {
      /**
       * The backend returned an unsuccessful response code.
       * The response body may contain clues as to what went wrong,
       */
      // console.error(
      //   `Backend returned code ${error.status}, ` +
      //   `body was: ${JSON.stringify(error.error, null, 4)}`);
    }
    /**
     * return an observable with a user-facing error message
     */
    return throwError(error.error);
  }
}
